import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Building2, Users, TrendingUp, BarChart3 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

function toBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function AdminCorporates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [form, setForm] = useState({ name: "", logo: "", address: "", contact_name: "", contact_email: "", contact_phone: "" });

  const load = () => api.get("/admin/workplaces").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (f) { const b64 = await toBase64(f); setForm((s) => ({ ...s, logo: b64 })); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/workplaces", form);
      toast.success("Workplace registered");
      setOpen(false);
      setForm({ name: "", logo: "", address: "", contact_name: "", contact_email: "", contact_phone: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const viewReport = async (id) => {
    try { const { data } = await api.get(`/admin/workplaces/${id}/report`); setReport(data); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="animate-fade-up" data-testid="admin-corporates">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Corporate partners</h1>
          <p className="mt-1 text-muted-foreground">Register workplaces and store co-branding logos.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-workplace-button" className="rounded-full bg-brand-green font-semibold text-white hover:bg-brand-green/90"><Plus className="mr-1 h-4 w-4" /> Register workplace</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Register a partner workplace</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Name</Label><Input required value={form.name} onChange={set("name")} data-testid="wp-name-input" className="mt-1" placeholder="Sasol N1 North" /></div>
              <div>
                <Label>Corporate logo</Label>
                <div className="mt-1 flex items-center gap-3">
                  {form.logo && <img src={form.logo} alt="" className="h-12 w-12 rounded-lg object-cover" />}
                  <Input type="file" accept="image/*" onChange={onFile} data-testid="wp-logo-file" />
                </div>
                <Input value={form.logo?.startsWith("data:") ? "" : form.logo} onChange={set("logo")} data-testid="wp-logo-url" className="mt-2" placeholder="…or paste a logo URL" />
              </div>
              <div><Label>Address</Label><Input value={form.address} onChange={set("address")} data-testid="wp-address-input" className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Contact name</Label><Input value={form.contact_name} onChange={set("contact_name")} data-testid="wp-contact-name" className="mt-1" /></div>
                <div><Label>Contact phone</Label><Input value={form.contact_phone} onChange={set("contact_phone")} data-testid="wp-contact-phone" className="mt-1" /></div>
              </div>
              <div><Label>Contact email</Label><Input value={form.contact_email} onChange={set("contact_email")} data-testid="wp-contact-email" className="mt-1" /></div>
              <DialogFooter><Button type="submit" disabled={busy} data-testid="wp-submit-button" className="rounded-full bg-brand-green font-semibold text-white hover:bg-brand-green/90">{busy ? "Saving…" : "Register"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((w) => (
          <div key={w.id} className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm transition-transform hover:-translate-y-1" data-testid="workplace-card">
            <div className="flex items-center gap-3">
              {w.logo ? <img src={w.logo} alt={w.name} className="h-12 w-12 rounded-xl object-cover" /> : <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-green/15 text-brand-greenLight"><Building2 className="h-6 w-6" /></div>}
              <div><p className="font-heading font-bold">{w.name}</p><p className="text-xs text-muted-foreground">{w.address || "—"}</p></div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground"><Users className="h-4 w-4" /> {w.worker_count} workers</div>
            <Button onClick={() => viewReport(w.id)} data-testid={`report-${w.id}`} variant="outline" size="sm" className="mt-4 w-full gap-2 rounded-full"><BarChart3 className="h-4 w-4" /> View report</Button>
          </div>
        ))}
        {items.length === 0 && <p className="col-span-full rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground">No partner workplaces yet.</p>}
      </div>

      <Dialog open={!!report} onOpenChange={(o) => !o && setReport(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{report?.workplace?.name} — report</DialogTitle></DialogHeader>
          {report && (
            <div className="grid grid-cols-2 gap-3">
              {[
                { l: "Workers", v: report.worker_count, i: Users },
                { l: "Tips", v: report.tip_count, i: TrendingUp },
                { l: "Total volume", v: `R${report.total_volume.toFixed(2)}`, i: TrendingUp },
                { l: "Avg tip", v: `R${report.avg_tip.toFixed(2)}`, i: BarChart3 },
              ].map((m) => (
                <div key={m.l} className="rounded-xl border border-border/60 bg-secondary/40 p-4">
                  <p className="overline-label text-muted-foreground">{m.l}</p>
                  <p className="mt-1 font-heading text-2xl font-extrabold text-brand-greenLight">{m.v}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
